import React, { useState, useEffect, useRef } from 'react';
import { User, Horse } from '@phosphor-icons/react';
import api from '../../utils/api';

/**
 * RiderAutocomplete — drop-in replacement for a regular rider-name input
 * that suggests previously-seen riders (within the same org) as the
 * producer types. Selecting a suggestion fills both rider + horse.
 *
 * Props:
 *   value: current rider name
 *   onChange: (value) => void — rider name updated
 *   onHorseChange: (value) => void — called when the user selects a
 *     suggestion that includes a horse name
 *   showId: scope for the backend query (shows the org's prior riders)
 *   placeholder, disabled, testId: forwarded to the input
 *   autoFocus: bool
 */
export const RiderAutocomplete = ({
  value, onChange, onHorseChange, showId,
  placeholder = 'Rider name', disabled = false,
  testId = 'rider-autocomplete', autoFocus = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Debounced fetch (200ms).
  useEffect(() => {
    if (!showId) return;
    const q = (value || '').trim();
    if (q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/shows/${showId}/rider-suggestions`, {
          params: { q },
        });
        setSuggestions(data.suggestions || []);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [value, showId]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (s) => {
    onChange(s.rider_name);
    if (onHorseChange && s.horse_name) onHorseChange(s.horse_name);
    setOpen(false);
    setSuggestions([]);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pick(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => value && value.length >= 2 && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
        data-testid={testId}
        autoComplete="off"
      />

      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-[#111] border border-[#262626] rounded-md shadow-2xl max-h-[260px] overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {suggestions.map((s, idx) => {
            const active = idx === activeIdx;
            return (
              <button
                key={`${s.rider_name}|${s.horse_name}|${idx}`}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors ${active ? 'bg-[#0096FF]/15' : 'hover:bg-[#0A0A0A]'}`}
                data-testid={`${testId}-option-${idx}`}
              >
                <User size={13} weight="duotone" className="text-[#0096FF] flex-shrink-0" />
                <span className="text-sm text-white flex-1 truncate">{s.rider_name}</span>
                {s.horse_name && (
                  <span className="flex items-center gap-1 text-[11px] text-[#9E9E9E] flex-shrink-0">
                    <Horse size={11} weight="duotone" className="text-[#FF9500]" />
                    {s.horse_name}
                  </span>
                )}
                {s.count > 1 && (
                  <span className="text-[10px] text-[#555] flex-shrink-0">×{s.count}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
